import { Slot } from "@radix-ui/react-slot";
import { createContext, useContext, type ComponentProps, type ReactNode } from "react";

import type { AssuntoDaFaixa } from "@/components/ui/anel-de-recorte";
import { Ladrilho, type IconeDoLadrilho } from "@/components/ui/ladrilho";
import { superficieDeVidroClass } from "@brevi/ui-kit/superficies";
import { cn } from "@/lib/utils";

/**
 * TUT-510 — o assunto que o `Panel` declarou, para o `PanelTitle` de dentro
 * desenhar o ladrilho. É contexto PRÓPRIO, e não o `FaixasDoAssunto` da
 * Visão geral: aquele também tinge as faixas e o anel lá dentro, e um painel
 * de lista que só quer o acento no título não pode recolorir uma faixa de
 * situação por tabela.
 */
const AssuntoDoPainelContext = createContext<AssuntoDaFaixa | null>(null);

type PanelProps = ComponentProps<"section"> & {
  asChild?: boolean;
  /**
   * TUT-218 — "form" dá respiro ao CORPO do painel (p-6, o mesmo padding do
   * `DialogContent` do ui-kit). Nasce como variante — e não vira o padrão —
   * porque o `Panel` tem 57 chamadores no app: a maioria entrega conteúdo que
   * já resolve o próprio respiro (`TableScroll`, `EmptyState`, listas com
   * padding próprio) e ganhar p-6 aqui de graça estufaria essas telas sem
   * pedido. A TUT-220 estendeu a variante aos painéis de conteúdo do portal
   * (Perfil, Configurações, Atendimento, resposta pública); o contrato é UM
   * corpo por painel — irmãos soltos empilham padding, embrulhe num filho só.
   */
  variant?: "default" | "form";
  /**
   * TUT-510 — o assunto do painel (Lumen): o `PanelTitle` de nível `h2` lá
   * dentro ganha o ladrilho. TUT-582 (dono, 02/11): o ladrilho é neutro — o
   * assunto escolhe só o glifo padrão; a cor de assunto foi aposentada.
   */
  assunto?: AssuntoDaFaixa;
};

/**
 * TUT-469 — fase 5 do redesenho (TUT-463): a moldura de TODAS as telas é o
 * vidro. O fundo com manchas está na tela inteira desde a TUT-467, e um painel
 * opaco sobre ele virava um buraco escuro no desenho; com o vidro, as telas de
 * cadastro, a ficha, o financeiro, as configurações e o Console ganham o mesmo
 * cartão da Visão geral (o `superficieDeVidro` da TUT-464), de uma vez, porque
 * é este o primitivo que as 85 telas usam. Cartão no canto do vidro (24px),
 * fundo `--vidro`, borda e sombra do vidro. O texto sobre ele já é medido em
 * cada mancha, nos dois temas (`contrast.test.ts`, TUT-464).
 */
export function Panel({ className, asChild = false, variant = "default", assunto, ...props }: PanelProps) {
  const Comp = asChild ? Slot : "section";
  const painel = (
    <Comp
      data-slot="panel"
      data-assunto={assunto}
      className={cn(
        // TUT-510 — a superfície de vidro tem nome no ui-kit (`superficieDeVidroClass`).
        superficieDeVidroClass,
        "panel min-w-0",
        // `:not(header)` poupa o `<PanelHeader>`, que já tem o próprio padding —
        // o corpo é sempre o único outro filho direto (FormGrid, ou a lista de
        // regras), então isto não empilha padding em cima de padding.
        variant === "form" ? "[&>:not(header)]:p-6" : null,
        className,
      )}
      {...props}
    />
  );
  // Revisão 1 (M3): o painel SEM assunto também provê (nulo). Um painel
  // aninhado sem assunto não herda o do painel de fora.
  return <AssuntoDoPainelContext.Provider value={assunto ?? null}>{painel}</AssuntoDoPainelContext.Provider>;
}

type PanelTitleProps = ComponentProps<"h2"> & {
  /** Nível do título; o cabeçalho de painel é `h2`, o bloco dentro dele, `h3`. */
  as?: "h2" | "h3";
  /** Ícone Lucide (ou equivalente) desenhado à esquerda, decorativo. */
  icon?: IconeDoLadrilho;
  /**
   * TUT-510 — o assunto do ladrilho. Sem a prop, vale o do `Panel` em volta;
   * o ladrilho só aparece no título `h2`. TUT-582: o cartão da Visão geral
   * (widget) não dá mais ladrilho ao título — lá o ícone é o simples.
   */
  assunto?: AssuntoDaFaixa;
};

/**
 * TUT-329 — o título de painel com ícone, numa linha só.
 *
 * O preflight do Tailwind (TUT-178) põe `svg { display: block }`, e o
 * `<h2><Icon />Texto</h2>` que as telas escreviam à mão empilhava o ícone EM
 * CIMA do texto (Assistentes, Advogados, Assuntos, Locadoras, Usuários…) — só
 * as telas que lembravam de pôr `flex items-center` no `<h2>` escapavam. O
 * dono viu o empilhamento em 27/09. A saída é o título saber se alinhar, e a
 * trava `panel-title.test.ts` reprova o ícone solto dentro de `<h2>`/`<h3>`.
 */
export function PanelTitle({ as = "h2", icon: Icon, assunto: assuntoProprio, className, children, ...props }: PanelTitleProps) {
  const Comp = as;
  const doPainel = useContext(AssuntoDoPainelContext);
  // TUT-510 — o ladrilho é o acento do TÍTULO do painel (h2). Um `h3` de
  // bloco interno segue com o ícone simples: dois ladrilhos no mesmo cartão
  // disputariam o acento.
  const assunto = as === "h2" ? (assuntoProprio ?? doPainel) : null;
  return (
    <Comp
      data-slot="panel-title"
      className={cn("flex min-w-0 items-center", assunto ? "gap-3" : "gap-2", className)}
      {...props}
    >
      {assunto ? (
        <Ladrilho assunto={assunto} icone={Icon} />
      ) : Icon ? (
        <Icon aria-hidden="true" className="flex-none" />
      ) : null}
      <span className="min-w-0">{children}</span>
    </Comp>
  );
}

type PanelHeaderProps = ComponentProps<"header"> & {
  asChild?: boolean;
  /**
   * O que fica à DIREITA do cabeçalho — um "Ver todos", um menu.
   *
   * ## Por que a prop existe, e o defeito que ela consertou
   *
   * TUT-266 fase 2. A Visão Geral Detalhada punha essa ação num `CardAction`,
   * que se posiciona com classes de GRADE (`col-start-2 row-start-1`) válidas
   * só dentro do `CardHeader`. Ao trocar a moldura por `Panel`, essas classes
   * viraram letra morta: o `variant="form"` é `flex-col`, então o botão
   * "Ver todos" cairia EMBAIXO da descrição, empurrando o conteúdo.
   *
   * **E nada reprovaria.** O TypeScript compila, a marcação é válida, os
   * testes passam — só o olho pega. Foi por isso que a prop nasceu em vez de
   * cada chamador remendar o `flex-direction` por fora: remendo por fora é
   * exatamente a descendente que a TUT-162 passou seis fatias recolhendo.
   *
   * No `variant` padrão a ação já ia para a direita sozinha (`justify-between`
   * com dois filhos); a prop serve aos dois de propósito, para o chamador não
   * precisar saber em qual variante ele está.
   */
  acoes?: ReactNode;
  /**
   * TUT-218 — par de `Panel variant="form"`: cabeçalho empilha título +
   * descrição (uma linha explicando o que o painel decide) e respira como o
   * `DialogHeader` do ui-kit (p-6, título maior). Fica atrás de uma variante
   * pelo mesmo motivo do `Panel` — 57 chamadores dependem do cabeçalho denso
   * atual (ex.: título + contagem lado a lado). Um painel pode combinar
   * cabeçalho denso com `Panel variant="form"` (TUT-220): a variante do corpo
   * e a do cabeçalho são independentes de propósito.
   */
  variant?: "default" | "form";
};

export function PanelHeader({
  className,
  asChild = false,
  variant = "default",
  acoes,
  children,
  ...props
}: PanelHeaderProps) {
  const Comp = asChild ? Slot : "header";
  return (
    <Comp
      data-slot="panel-header"
      className={cn(
        // `panel-header` continua no className: várias telas ainda miram o
        // cabeçalho por contexto (`.next-action-panel .panel-header > svg` e
        // afins) a partir do container.
        "panel-header flex",
        // TUT-510 — o respiro do Lumen (acabamento leve): o cabeçalho padrão
        // perde o fio de baixo e ganha ar em cima, como o cartão da Visão geral
        // (TUT-312 tirou o mesmo fio dos widgets: "um risco entre o título e o
        // conteúdo"). O recuo lateral NÃO muda: 0,9rem é o recuo da célula da
        // tabela (0,875rem), e o título fica alinhado à primeira coluna, que é
        // o que a tabela suave da TUT-509 conta. O `form` guarda o fio: lá o
        // corpo tem p-6, e sem o fio sobrariam 48px de vão sem divisa.
        variant === "form"
          ? "flex-col items-start gap-1 border-b border-border px-6 py-5"
          : // `flex-wrap`: com o ladrilho, o título e a linha ao lado (contagem,
            // subtítulo) não cabem juntos no celular, e o título espremido virava
            // uma palavra por linha ("Diligências / por / clientes"). Quebrando,
            // o vizinho desce para baixo do título.
            "min-h-[3.4rem] flex-wrap items-center justify-between gap-x-3 gap-y-1 px-[0.9rem] pt-[0.85rem] pb-[0.35rem]",
        // TUT-162 fatia 5 — estes três são DEFAULT, não decreto.
        //
        // A forma antiga (variante descendente sem `:where`) gera `.classe h2`,
        // especificidade 0,2,1, e vencia a classe posta
        // no próprio `<h2>` (0,1,0): 9 chamadores pediam tamanho e recebiam
        // 16px — `DashboardGrid`, seis pontos do Console e dois do portal.
        //
        // A saída NÃO é `:not([class*='text-'])`, a guarda que resolveu o
        // tamanho de ícone na fatia 2: ali `size-` só quer dizer uma coisa;
        // aqui `text-` é prefixo de cor E de tamanho, e um `<h2>` que pedisse
        // só a cor escaparia também do tamanho, caindo no `1.5em` do navegador.
        //
        // `:where(&)` zera a especificidade do lado do PAI — o seletor vira
        // `:where(.classe) h2`, 0,0,1. Qualquer classe no `<h2>` (0,1,0) vence,
        // sem o componente precisar adivinhar QUAL propriedade o chamador quer
        // trocar. É default de verdade, e é o mecanismo que faltava para as
        // descendentes que o `component-map.md` chama de armadilha.
        //
        // TUT-472 — a tracking negativa encolhe também o ESPAÇO, que em Sora
        // pequena já é estreito: "Atribuídos a mim" se lia "Atribuídosa mim".
        // O `word-spacing` alarga o espaço além do que a tracking tirou (a
        // tracking tira ~0,3px; 0.12em soma ~1,8px), para a palavra separar.
        "[:where(&)_h2]:m-0 [:where(&)_h2]:tracking-[-0.022em] [:where(&)_h2]:[word-spacing:0.12em]",
        variant === "form"
          ? "[:where(&)_h2]:text-titulo [:where(&)_h2]:font-semibold [:where(&)_p]:m-0 [:where(&)_p]:text-corpo [:where(&)_p]:text-[var(--muted)]"
          : "[:where(&)_h2]:text-subtitulo",
        // TUT-325 — só o LINK comum ganha o tamanho de link do cabeçalho. O
        // `Button asChild` também vira `<a>` filho direto e herdava 0,78rem: o
        // "Abrir processo" do Atendimento saía menor que todo `quiet` do app.
        "[&>a:not([data-slot=button])]:text-legenda [&>a:not([data-slot=button])]:text-[var(--link)]",
        // Com ação, a coluna do `form` vira LINHA: título e descrição
        // empilhados à esquerda, ação à direita. Sem ação, nada muda.
        acoes && variant === "form" && "flex-row items-start justify-between gap-3",
        className,
      )}
      {...props}
    >
      {acoes ? (
        <>
          <div className="flex min-w-0 flex-col gap-1">{children}</div>
          <div className="flex flex-none items-center gap-1">{acoes}</div>
        </>
      ) : (
        children
      )}
    </Comp>
  );
}
